import React from 'react';
import { useOrdenesTrabajoContext } from '@/contexts/OrdenesTrabajoContext';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ClipboardList, Clock, AlertTriangle, CheckCircle, Calendar, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export const TecnicoDashboard = () => {
  const { ordenes, loading } = useOrdenesTrabajoContext();
  const { user } = useAuth();
  const navigate = useNavigate();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Cargando órdenes de trabajo...</p>
        </div>
      </div>
    );
  }

  // Filter orders for the current technician
  const misOrdenes = ordenes.filter(orden => orden.tecnico_asignado_id === user?.id);
  
  const ordenesPendientes = misOrdenes.filter(o => ['asignada', 'en_progreso'].includes(o.estado));
  const ordenesUrgentes = misOrdenes.filter(o => o.prioridad === 'urgente' && ['asignada', 'en_progreso'].includes(o.estado));
  const ordenesHoy = misOrdenes.filter(o => {
    const hoy = new Date().toISOString().split('T')[0];
    return o.fecha_limite === hoy && ['asignada', 'en_progreso'].includes(o.estado);
  });

  const getPrioridadColor = (prioridad: string) => {
    switch (prioridad) {
      case 'urgente': return 'destructive';
      case 'alta': return 'destructive';
      case 'media': return 'secondary';
      case 'baja': return 'outline';
      default: return 'outline';
    }
  };

  const getEstadoColor = (estado: string) => {
    switch (estado) {
      case 'asignada': return 'secondary';
      case 'en_progreso': return 'secondary';
      case 'finalizada': return 'outline';
      case 'cancelada': return 'destructive';
      default: return 'outline';
    }
  };

  const handleVerOrden = (ordenId: string) => {
    navigate(`/ordenes-trabajo?orden=${ordenId}`);
  };

  const handleCrearParte = () => {
    navigate('/partes-trabajo');
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
            Mi Dashboard
          </h1>
          <p className="text-muted-foreground">
            Gestiona tus órdenes de trabajo asignadas
          </p>
        </div>
        <Button 
          onClick={handleCrearParte}
          className="flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          Nuevo Parte de Trabajo
        </Button>
      </div>

      {/* Métricas principales */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Total Asignadas
            </CardTitle>
            <ClipboardList className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{misOrdenes.length}</div>
            <p className="text-xs text-muted-foreground">
              órdenes de trabajo
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Pendientes
            </CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{ordenesPendientes.length}</div>
            <p className="text-xs text-muted-foreground">
              por completar
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Urgentes
            </CardTitle>
            <AlertTriangle className="h-4 w-4 text-destructive" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-destructive">{ordenesUrgentes.length}</div>
            <p className="text-xs text-muted-foreground">
              requieren atención
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Vencen Hoy
            </CardTitle>
            <Calendar className="h-4 w-4 text-warning" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-warning">{ordenesHoy.length}</div>
            <p className="text-xs text-muted-foreground">
              fecha límite
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Lista de órdenes pendientes */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ClipboardList className="h-5 w-5" />
            Órdenes Pendientes
          </CardTitle>
          <CardDescription>
            Órdenes de trabajo asignadas que requieren tu atención
          </CardDescription>
        </CardHeader>
        <CardContent>
          {ordenesPendientes.length === 0 ? (
            <div className="text-center py-8">
              <CheckCircle className="h-16 w-16 text-success mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">¡Excelente trabajo!</h3>
              <p className="text-muted-foreground">
                No tienes órdenes pendientes en este momento
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {ordenesPendientes.slice(0, 5).map((orden) => (
                <div 
                  key={orden.id}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h4 className="font-semibold">{orden.numero_ot}</h4>
                      <Badge variant={getPrioridadColor(orden.prioridad)}>
                        {orden.prioridad}
                      </Badge>
                      <Badge variant={getEstadoColor(orden.estado)}>
                        {orden.estado.replace('_', ' ')}
                      </Badge>
                    </div>
                    <p className="text-sm font-medium">{orden.titulo}</p>
                    <p className="text-sm text-muted-foreground">
                      {orden.cliente?.nombre}
                    </p>
                    {orden.fecha_limite && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Fecha límite: {new Date(orden.fecha_limite).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleVerOrden(orden.id)}
                  >
                    Ver Detalles
                  </Button>
                </div>
              ))}
              
              {ordenesPendientes.length > 5 && (
                <div className="text-center pt-4">
                  <Button
                    variant="outline"
                    onClick={() => navigate('/ordenes-trabajo')}
                  >
                    Ver todas las órdenes ({ordenesPendientes.length})
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};